import { createSeededNoise } from "./perlinNoise";
import { fbm01 } from "./flowAngleNoise";

export interface DomainWarpParams {
    canvasW: number;
    canvasH: number;
    sampleStep: number;
    seed: string;
    /** Base frequency for the final value noise (noise-space units per canvas pixel). */
    noiseScale: number;
    /** Frequency for the warp-offset noise evaluated at the original canvas position. */
    warpScale: number;
    /** Maximum displacement per warp iteration, in canvas pixels. */
    warpStrength: number;
    /** How many times to feed the field back into itself (1–3). */
    warpIterations: number;
    /** Number of FBM octaves used for every noise evaluation. */
    octaves: number;
}

/**
 * Compute a domain-warped scalar field sampled at `sampleStep` pixel intervals.
 *
 * Domain warping repeatedly displaces sampling coordinates using offset noise
 * before the final value lookup: f(p) = noise(p + K·noise(p + K·noise(p))).
 * This produces complex swirling, geological-looking iso-contours.
 *
 * @returns 2D array `field[row][col]` with values in [0, 1], border-zeroed so
 *          every iso-contour produced by marchingSquares closes within the canvas.
 */
export function computeDomainWarpField(params: DomainWarpParams): number[][] {
    const {
        canvasW,
        canvasH,
        sampleStep,
        seed,
        noiseScale,
        warpScale,
        warpStrength,
        warpIterations,
        octaves,
    } = params;

    // Independent noise functions for each warp axis and final value
    const noiseQx = createSeededNoise(seed + "_qx");
    const noiseQy = createSeededNoise(seed + "_qy");
    const noiseRx = createSeededNoise(seed + "_rx");
    const noiseRy = createSeededNoise(seed + "_ry");
    const noiseTx = createSeededNoise(seed + "_tx");
    const noiseTy = createSeededNoise(seed + "_ty");
    const noiseV  = createSeededNoise(seed + "_v");

    const sampleCols = Math.ceil(canvasW / sampleStep) + 1;
    const sampleRows = Math.ceil(canvasH / sampleStep) + 1;

    // Warp displacement K: warpStrength canvas-pixels expressed in noise-space units
    const K = warpStrength * noiseScale;

    const field: number[][] = [];

    for (let r = 0; r < sampleRows; r++) {
        field[r] = new Array<number>(sampleCols).fill(0);
        const sy = r * sampleStep;

        for (let c = 0; c < sampleCols; c++) {
            const sx = c * sampleStep;

            // Start in noise-space coordinates for the final value lookup
            let px = sx * noiseScale;
            let py = sy * noiseScale;

            // Iteration 1 – warp offsets evaluated at the original canvas position
            // using warpScale, so users can vary warp frequency independently of value frequency
            if (warpIterations >= 1) {
                const qx = fbm01(noiseQx, sx * warpScale, sy * warpScale, octaves) - 0.5;
                const qy = fbm01(noiseQy, sx * warpScale, sy * warpScale, octaves) - 0.5;
                px += K * qx;
                py += K * qy;
            }

            // Iteration 2 – warp offsets evaluated at the already-warped position
            if (warpIterations >= 2) {
                const rx = fbm01(noiseRx, px, py, octaves) - 0.5;
                const ry = fbm01(noiseRy, px, py, octaves) - 0.5;
                px += K * rx;
                py += K * ry;
            }

            // Iteration 3
            if (warpIterations >= 3) {
                const tx = fbm01(noiseTx, px, py, octaves) - 0.5;
                const ty = fbm01(noiseTy, px, py, octaves) - 0.5;
                px += K * tx;
                py += K * ty;
            }

            field[r][c] = fbm01(noiseV, px, py, octaves);
        }
    }

    // Zero the border to guarantee every iso-contour closes within the canvas
    // (same technique as computeScalarField in marchingSquares.ts)
    const lastRow = sampleRows - 1;
    const lastCol = sampleCols - 1;
    for (let c = 0; c < sampleCols; c++) {
        field[0][c] = 0;
        field[lastRow][c] = 0;
    }
    for (let r = 0; r < sampleRows; r++) {
        field[r][0] = 0;
        field[r][lastCol] = 0;
    }

    return field;
}
